import { type FC, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageTitle } from "../../../components/PageTitle";
import {
  type UpdateUserInput,
  useAdminUserApi,
  useToggleSystemAdminApi,
  useUpdateAdminUserApi,
} from "../../../hooks/useAdminApi";

export const UserDetailPage: FC = () => {
  const { id } = useParams<{ id: string }>();

  const {
    data: user,
    loading,
    error,
    refetch,
  } = useAdminUserApi({
    id: id || "",
    skip: !id,
  });
  const { updateUser } = useUpdateAdminUserApi();
  const { toggleSystemAdmin } = useToggleSystemAdminApi();

  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<UpdateUserInput>({});
  const [actionLoading, setActionLoading] = useState(false);

  const handleEdit = () => {
    setEditForm({
      familyName: user?.familyName,
      givenName: user?.givenName,
      familyNameKana: user?.familyNameKana || undefined,
      givenNameKana: user?.givenNameKana || undefined,
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!id) return;
    setActionLoading(true);
    try {
      await updateUser(id, editForm);
      await refetch();
      setIsEditing(false);
    } catch (e) {
      alert(`エラー: ${e instanceof Error ? e.message : "不明なエラー"}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleSystemAdmin = async () => {
    if (!id || !user) return;
    if (
      !window.confirm(
        user.isSystemAdmin
          ? "このユーザーのシステム管理者権限を剥奪しますか？"
          : "このユーザーにシステム管理者権限を付与しますか？",
      )
    ) {
      return;
    }

    setActionLoading(true);
    try {
      await toggleSystemAdmin(id, !user.isSystemAdmin);
      await refetch();
    } catch (e) {
      alert(`エラー: ${e instanceof Error ? e.message : "不明なエラー"}`);
    } finally {
      setActionLoading(false);
    }
  };

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6">
        <PageTitle>ユーザー詳細</PageTitle>
        <div className="alert alert-error">
          <svg
            className="stroke-current shrink-0 h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>エラーが発生しました: {error.message}</span>
        </div>
      </div>
    );
  }

  if (loading || !user) {
    return (
      <div className="container mx-auto px-4 py-6">
        <PageTitle>ユーザー詳細</PageTitle>
        <div className="flex justify-center py-8">
          <span className="loading loading-spinner loading-lg"></span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <PageTitle>ユーザー詳細</PageTitle>
        <Link to="/admin/users" className="btn btn-ghost btn-sm">
          ← ユーザー一覧に戻る
        </Link>
      </div>

      {/* ユーザー情報 */}
      <div className="card bg-base-100 shadow-md mb-6">
        <div className="card-body">
          <div className="flex justify-between items-start mb-4">
            <h2 className="card-title text-color-title">ユーザー情報</h2>
            <div className="flex gap-2">
              {!isEditing && (
                <>
                  <button
                    onClick={handleEdit}
                    className="btn btn-primary btn-sm"
                  >
                    編集
                  </button>
                  <button
                    onClick={handleToggleSystemAdmin}
                    disabled={actionLoading}
                    className={`btn btn-sm ${
                      user.isSystemAdmin ? "btn-warning" : "btn-secondary"
                    }`}
                  >
                    {user.isSystemAdmin ? "権限を剥奪" : "管理者に昇格"}
                  </button>
                </>
              )}
            </div>
          </div>

          {isEditing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">姓</span>
                  </label>
                  <input
                    type="text"
                    value={editForm.familyName || ""}
                    onChange={e =>
                      setEditForm({ ...editForm, familyName: e.target.value })
                    }
                    className="input input-bordered w-full"
                  />
                </div>
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">名</span>
                  </label>
                  <input
                    type="text"
                    value={editForm.givenName || ""}
                    onChange={e =>
                      setEditForm({ ...editForm, givenName: e.target.value })
                    }
                    className="input input-bordered w-full"
                  />
                </div>
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">姓（カナ）</span>
                  </label>
                  <input
                    type="text"
                    value={editForm.familyNameKana || ""}
                    onChange={e =>
                      setEditForm({
                        ...editForm,
                        familyNameKana: e.target.value,
                      })
                    }
                    className="input input-bordered w-full"
                  />
                </div>
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">名（カナ）</span>
                  </label>
                  <input
                    type="text"
                    value={editForm.givenNameKana || ""}
                    onChange={e =>
                      setEditForm({
                        ...editForm,
                        givenNameKana: e.target.value,
                      })
                    }
                    className="input input-bordered w-full"
                  />
                </div>
              </div>
              <div className="flex gap-4">
                <button
                  onClick={handleSave}
                  disabled={actionLoading}
                  className="btn btn-primary"
                >
                  {actionLoading ? (
                    <span className="loading loading-spinner loading-sm"></span>
                  ) : (
                    "保存"
                  )}
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  disabled={actionLoading}
                  className="btn btn-ghost"
                >
                  キャンセル
                </button>
              </div>
            </div>
          ) : (
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">名前</dt>
                <dd className="text-gray-900">
                  {user.familyName} {user.givenName}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  名前（カナ）
                </dt>
                <dd className="text-gray-900">
                  {user.familyNameKana && user.givenNameKana
                    ? `${user.familyNameKana} ${user.givenNameKana}`
                    : "-"}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  メールアドレス
                </dt>
                <dd className="text-gray-900">{user.email}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">権限</dt>
                <dd>
                  {user.isSystemAdmin ? (
                    <span className="badge badge-secondary">
                      システム管理者
                    </span>
                  ) : (
                    <span className="badge badge-ghost">一般ユーザー</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">作成日</dt>
                <dd className="text-gray-900">
                  {new Date(user.createdAt).toLocaleString("ja-JP")}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">更新日</dt>
                <dd className="text-gray-900">
                  {new Date(user.updatedAt).toLocaleString("ja-JP")}
                </dd>
              </div>
            </dl>
          )}
        </div>
      </div>

      {/* 所属テナント一覧 */}
      <div className="card bg-base-100 shadow-md">
        <div className="card-body">
          <h2 className="card-title text-color-title">
            所属テナント ({user.tenants.length}件)
          </h2>
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>テナント名</th>
                  <th>ロール</th>
                </tr>
              </thead>
              <tbody>
                {user.tenants.length === 0 ? (
                  <tr>
                    <td colSpan={2} className="text-center text-gray-500">
                      所属テナントがありません
                    </td>
                  </tr>
                ) : (
                  user.tenants.map(tenant => (
                    <tr key={tenant.tenantId} className="hover">
                      <td>
                        <Link
                          to={`/admin/tenants/${tenant.tenantId}`}
                          className="link link-primary"
                        >
                          {tenant.tenantName}
                        </Link>
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            tenant.role === "owner"
                              ? "badge-secondary"
                              : tenant.role === "admin"
                                ? "badge-primary"
                                : "badge-ghost"
                          }`}
                        >
                          {tenant.role}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetailPage;
